"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Literata } from "next/font/google";

import ReadingControls from "@/components/notes/ReadingControls";
import {
  DEFAULT_PREFS,
  READING_PREFS_KEY,
  loadPrefs,
  savePrefs,
  type ReadingPrefs,
} from "@/lib/reading-prefs";

// Kindle's serif, loaded only where the reading surface renders. The variable
// is applied to the surface and to the portaled controls, so both resolve it.
const literata = Literata({
  subsets: ["latin"],
  style: ["normal", "italic"],
  variable: "--font-literata",
});

/**
 * Runs while the HTML is parsed, before first paint, so a returning reader
 * never sees a flash of the default theme. It patches the same attributes
 * React renders, and the lazy useState below reads the same localStorage
 * value, so hydration agrees with the patched DOM.
 */
const INIT_SCRIPT = `(function(){try{var p=JSON.parse(localStorage.getItem(${JSON.stringify(
  READING_PREFS_KEY,
)}));var e=document.currentScript.parentElement;if(typeof p.theme==="string")e.setAttribute("data-reading-theme",p.theme);if(typeof p.font==="string")e.setAttribute("data-reading-font",p.font);if(typeof p.leading==="string")e.setAttribute("data-reading-leading",p.leading);if(typeof p.fontSize==="number")e.style.setProperty("--reading-font-size",p.fontSize+"px");}catch(_){}})()`;

export default function ReadingSurface({
  children,
}: {
  children: React.ReactNode;
}) {
  const [prefs, setPrefs] = useState<ReadingPrefs>(() =>
    typeof window === "undefined" ? DEFAULT_PREFS : loadPrefs(),
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const update = useCallback((patch: Partial<ReadingPrefs>) => {
    setPrefs((current) => {
      const next = { ...current, ...patch };
      savePrefs(next);
      return next;
    });
  }, []);

  // Warmth tints light themes down (multiply) but would only blacken the
  // dark theme, so there it lifts the page toward amber instead (screen).
  const warmthOpacity =
    (prefs.warmth / 100) * (prefs.theme === "dark" ? 0.18 : 0.35);

  return (
    <div
      className={`reading-surface min-h-screen ${literata.variable}`}
      data-reading-theme={prefs.theme}
      data-reading-font={prefs.font}
      data-reading-leading={prefs.leading}
      style={
        { "--reading-font-size": `${prefs.fontSize}px` } as React.CSSProperties
      }
      suppressHydrationWarning
    >
      <script dangerouslySetInnerHTML={{ __html: INIT_SCRIPT }} />
      {children}
      {mounted &&
        createPortal(
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[100] transition-opacity duration-200"
            style={{
              backgroundColor: "#ff8c1a",
              mixBlendMode: prefs.theme === "dark" ? "screen" : "multiply",
              opacity: warmthOpacity,
            }}
          />,
          document.body,
        )}
      {mounted &&
        createPortal(
          <div data-reading-theme={prefs.theme} className={literata.variable}>
            <ReadingControls prefs={prefs} onChange={update} />
          </div>,
          document.body,
        )}
    </div>
  );
}
